import React from 'react'
import { LayoutProps } from 'src/layouts/types'
import useResponsive from 'src/hooks/useResponsive'
import ScrollToTop from 'src/components/ui/scroll-to-top'

import Navigation from './components/vertical/navigation'
import GlobalSearchComponent from './components/GlobalSearchComponent'

import Fab from '@mui/material/Fab'
import Stack from '@mui/material/Stack'
import Icon from 'src/components/ui/icon'
import { styled } from '@mui/material/styles'
import { useSearchQuery } from 'src/hooks/useSearchQuery'
import { SearchAsyncModal } from 'src/modals/SearchAsyncModal'
import { useAccountSelect } from 'src/hooks/useAccount'
import {
  AccountsType,
  useSelectedAccounts,
} from 'src/hooks/useSelectedAccounts'
import { AssinstantWindow } from 'src/components/widgets/Assistant'
import { useAssistantSelect } from 'src/hooks/useAssistant'
import { useLeftNavTheme } from 'src/context/leftNavThemeContext'
import { drawerWidth, LayoutContext } from 'src/layouts/layoutContext'

const glassNoiseBackground =
  'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27140%27 height=%27140%27 viewBox=%270 0 140 140%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.72%27 numOctaves=%273%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27140%27 height=%27140%27 filter=%27url(%23n)%27 opacity=%270.18%27/%3E%3C/svg%3E")'

const Main = styled('main', {
  shouldForwardProp: (prop) =>
    !['open', 'isMobile', 'modalOpened', 'isGlassTheme'].includes(String(prop)),
})<{
  open?: boolean
  isMobile: boolean
  modalOpened?: boolean
  isGlassTheme?: boolean
}>(({ theme, open, modalOpened, isMobile, isGlassTheme }) => ({
  width: '100vw',
  height: '100vh',

  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: isGlassTheme ? '#F7F8FB' : undefined,
  backgroundImage: isGlassTheme
    ? `${glassNoiseBackground},
       radial-gradient(circle at 12% 14%, rgba(11, 214, 141, 0.115) 0, rgba(11, 214, 141, 0.075) 8%, rgba(11, 214, 141, 0) 20%),
       radial-gradient(circle at 86% 10%, rgba(34, 141, 255, 0.11) 0, rgba(34, 141, 255, 0.07) 9%, rgba(34, 141, 255, 0) 22%),
       radial-gradient(circle at 72% 88%, rgba(87, 78, 249, 0.075) 0, rgba(87, 78, 249, 0.05) 8%, rgba(87, 78, 249, 0) 20%),
       linear-gradient(180deg, #fbfbfd 0%, #f3f6fb 100%)`
    : undefined,
  backgroundBlendMode: isGlassTheme
    ? 'soft-light, normal, normal, normal, normal'
    : undefined,
  backgroundSize: isGlassTheme
    ? '140px 140px, auto, auto, auto, auto'
    : undefined,
  '--app-glass-surface': isGlassTheme
    ? 'rgba(255, 255, 255, 0.66)'
    : theme.palette.background.paper,
  '--app-glass-surface-strong': isGlassTheme
    ? 'rgba(255, 255, 255, 0.78)'
    : theme.palette.background.paper,
  '--app-glass-border': isGlassTheme
    ? 'rgba(255, 255, 255, 0.7)'
    : theme.palette.divider,
  '--app-glass-shadow': isGlassTheme
    ? '0 4px 12px rgba(34, 47, 62, 0.045)'
    : theme.shadows[2],
  '--app-glass-inner-border': isGlassTheme
    ? 'inset 0 0 0 1px rgba(255, 255, 255, 0.44)'
    : 'none',
  '--app-glass-blur': isGlassTheme ? 'blur(18px) saturate(145%)' : 'none',
  '--app-widget-radius': '8px',
  '--app-widget-surface': isGlassTheme
    ? 'rgba(255, 255, 255, 0.72)'
    : theme.palette.background.paper,
  '--app-widget-content-surface': isGlassTheme
    ? '#ffffff'
    : theme.palette.background.paper,
  '--app-widget-header-surface': isGlassTheme
    ? 'rgba(255, 255, 255, 0.58)'
    : theme.palette.background.paper,
  '--app-widget-shadow': isGlassTheme
    ? '0 4px 12px rgba(34, 47, 62, 0.045)'
    : theme.shadows[2],
  '--app-scroll-sheet-surface': isGlassTheme
    ? 'rgba(255, 255, 255, 0.52)'
    : theme.palette.background.paper,
  '--app-scroll-sheet-blur': isGlassTheme
    ? 'blur(24px) saturate(165%)'
    : 'none',
  '--app-widget-header-glass': isGlassTheme
    ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.86) 0%, rgba(255, 255, 255, 0.78) 100%)'
    : theme.palette.background.paper,
  '--app-widget-subheader-glass': isGlassTheme
    ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.78) 0%, rgba(255, 255, 255, 0.68) 100%)'
    : theme.palette.background.paper,
  '--app-hover-bg': isGlassTheme
    ? 'rgba(36, 50, 61, 0.055)'
    : theme.palette.action.hover,
  '--app-hover-border': 'transparent',
  '--app-hover-shadow': 'none',
  '--app-hover-color': '#24323D',
  '--app-active-bg': isGlassTheme
    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.84) 0%, rgba(255, 255, 255, 0.56) 100%)'
    : theme.palette.action.selected,
  '--app-active-border': 'rgba(11, 214, 141, 0.24)',
  '--app-active-shadow': isGlassTheme
    ? '0 2px 6px rgba(34, 47, 62, 0.035), inset 0 0 0 1px rgba(11, 214, 141, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.72)'
    : 'none',
  '--app-active-color': '#0A7E5B',
  '--app-active-accent': '#0BD68D',
  '--app-menu-hover-bg': 'var(--app-hover-bg)',
  '--app-menu-hover-border': 'var(--app-hover-border)',
  '--app-menu-hover-shadow': 'var(--app-hover-shadow)',
  '--app-menu-active-bg': 'var(--app-active-bg)',
  '--app-menu-active-border': 'var(--app-active-border)',
  '--app-menu-active-shadow': 'var(--app-active-shadow)',
  '--app-menu-active-color': 'var(--app-active-color)',
  '--app-avatar-bg': isGlassTheme
    ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.82) 0%, rgba(246, 250, 249, 0.68) 100%)'
    : theme.palette.customColors.avatarBg,
  '--app-avatar-color': isGlassTheme ? '#32423E' : theme.palette.grey[100],
  '--app-avatar-border': isGlassTheme
    ? 'rgba(11, 214, 141, 0.18)'
    : 'transparent',
  '--app-avatar-shadow': isGlassTheme
    ? 'inset 0 0 0 1px rgba(255, 255, 255, 0.72), 0 1px 3px rgba(34, 47, 62, 0.035)'
    : 'none',
  '--app-avatar-blur': 'none',

  ...(isGlassTheme
    ? {
        '& .MuiPaper-root:not(.MuiDrawer-paper):not(.MuiAccordion-root)': {
          backgroundColor: 'var(--app-widget-surface)',
          backgroundImage: 'none',
          border: '1px solid var(--app-glass-border)',
          borderRadius: 'var(--app-widget-radius)',
          boxShadow: 'var(--app-widget-shadow), var(--app-glass-inner-border)',
          overflow: 'hidden',
        },
        '& .app-glass-surface': {
          backgroundColor: 'var(--app-glass-surface-strong)',
          backdropFilter: 'var(--app-glass-blur)',
          WebkitBackdropFilter: 'var(--app-glass-blur)',
        },
        '& .MuiTableContainer-root, & .MuiDataGrid-root': {
          backgroundColor: 'var(--app-widget-surface)',
          backgroundImage: 'none',
          borderColor: 'var(--app-glass-border)',
          borderRadius: 'var(--app-widget-radius)',
          boxShadow: 'var(--app-widget-shadow), var(--app-glass-inner-border)',
        },
        '& [data-object-page="true"] .MuiAccordion-root, & [data-object-page="true"] .MuiAccordion-root.Mui-expanded':
          {
            backgroundColor: 'var(--app-widget-surface)',
            backgroundImage: 'none',
            border: '1px solid var(--app-glass-border)',
            borderRadius: 'var(--app-widget-radius)',
            boxShadow:
              'var(--app-widget-shadow), var(--app-glass-inner-border)',
            overflow: 'hidden',
          },
        '& [data-object-page="true"] .MuiAccordionSummary-root, & [data-object-page="true"] .MuiAccordionSummary-root.Mui-expanded':
          {
            background: 'var(--app-widget-header-surface)',
            boxShadow: 'none',
            borderBottom: '1px solid rgba(36, 50, 61, 0.06)',
            transition:
              'background-color 160ms ease, background 160ms ease, border-color 160ms ease',
          },
        '& [data-object-page="true"] .MuiAccordionSummary-root:hover': {
          background: 'rgba(255, 255, 255, 0.7)',
          boxShadow: 'none',
        },
        '& [data-object-page="true"] .MuiAccordionSummary-root.Mui-expanded:hover':
          {
            background: 'rgba(255, 255, 255, 0.7)',
            boxShadow: 'none',
          },
        '& [data-object-page="true"] .MuiAccordionDetails-root': {
          background: 'transparent',
        },
        '& [data-object-page="true"] .object-page-widget.MuiAccordion-root, & [data-object-page="true"] .object-page-widget.MuiAccordion-root.Mui-expanded':
          {
            position: 'relative',
            flexShrink: 0,
            overflow: 'hidden',
            backgroundColor: 'var(--app-widget-content-surface)',
            backgroundImage: 'none',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
            border: '1px solid var(--app-glass-border)',
            borderRadius: 'var(--app-widget-radius)',
            boxShadow:
              'var(--app-widget-shadow), var(--app-glass-inner-border)',
          },
        '& [data-object-page="true"] .object-page-widget.MuiAccordion-root::before':
          {
            display: 'none',
          },
        '& [data-object-page="true"] .object-page-widget-summary.MuiAccordionSummary-root, & [data-object-page="true"] .object-page-widget-summary.MuiAccordionSummary-root.Mui-expanded':
          {
            minHeight: { xs: 44, md: 52 },
            background: 'var(--app-widget-header-glass)',
            backdropFilter: 'var(--app-scroll-sheet-blur)',
            WebkitBackdropFilter: 'var(--app-scroll-sheet-blur)',
            borderBottom: '1px solid rgba(36, 50, 61, 0.06)',
            boxShadow:
              'inset 0 1px 0 rgba(255, 255, 255, 0.96), inset 0 -1px 0 rgba(255, 255, 255, 0.48), 0 2px 6px rgba(34, 47, 62, 0.035)',
            transition:
              'background-color 160ms ease, background 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
          },
        '& [data-object-page="true"] .object-page-widget-summary.Mui-disabled':
          {
            opacity: 1,
          },
        '& [data-object-page="true"] .object-page-widget-summary .MuiAccordionSummary-content, & [data-object-page="true"] .object-page-widget-summary.Mui-expanded .MuiAccordionSummary-content':
          {
            margin: { xs: '8px 0', md: '10px 0' },
            alignItems: 'center',
          },
        '& [data-object-page="true"] .object-page-widget-summary:hover': {
          background:
            'linear-gradient(180deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.8) 100%)',
        },
        '& [data-object-page="true"] .object-page-widget-summary .MuiAccordionSummary-expandIconWrapper':
          {
            color: 'text.secondary',
          },
        '& [data-object-page="true"] .object-page-widget-details.MuiAccordionDetails-root':
          {
            position: 'relative',
            background: 'var(--app-widget-content-surface)',
            backgroundImage: 'none',
          },
        '& [data-object-page="true"] .object-page-widget-details .MuiTableContainer-root':
          {
            backgroundColor: 'transparent',
            backgroundImage: 'none',
            border: 0,
            borderRadius: 0,
            boxShadow: 'none',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          },
        '& [data-object-page="true"] .object-widget-glass-header, & [data-object-page="true"] .object-widget-glass-subheader':
          {
            backdropFilter: 'var(--app-scroll-sheet-blur)',
            WebkitBackdropFilter: 'var(--app-scroll-sheet-blur)',
            borderColor: 'rgba(255, 255, 255, 0.78)',
          },
        '& [data-object-page="true"] .object-widget-glass-header': {
          background: 'var(--app-widget-header-glass)',
          zIndex: 50,
          boxShadow:
            'inset 0 1px 0 rgba(255, 255, 255, 0.96), inset 0 -1px 0 rgba(255, 255, 255, 0.48), 0 2px 6px rgba(34, 47, 62, 0.035)',
        },
        '& [data-object-page="true"] .object-widget-glass-subheader': {
          background: 'var(--app-widget-subheader-glass)',
          zIndex: 40,
          boxShadow:
            'inset 0 1px 0 rgba(255, 255, 255, 0.78), inset 0 -1px 0 rgba(255, 255, 255, 0.34), 0 1px 4px rgba(34, 47, 62, 0.032)',
        },
        '& [data-object-page="true"] .object-widget-scroll-frame': {
          position: 'relative',
          minHeight: 0,
          overflow: 'hidden',
          background: 'transparent',
        },
        '& [data-object-page="true"] .object-widget-scroll': {
          position: 'relative',
        },
        '& [data-object-page="true"] .object-widget-scroll--table .MuiTableHead-root':
          {
            position: 'sticky',
            top: 0,
            zIndex: 10,
          },
        '& [data-object-page="true"] .object-widget-scroll--table .MuiTableCell-head':
          {
            backgroundColor: 'var(--app-scroll-sheet-surface)',
            backdropFilter: 'var(--app-scroll-sheet-blur)',
            WebkitBackdropFilter: 'var(--app-scroll-sheet-blur)',
            boxShadow: '0 1px 0 rgba(36, 50, 61, 0.06)',
          },
        '& .MuiTableCell-head, & .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader':
          {
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
          },
        '& .MuiDataGrid-cell': {
          borderColor: 'rgba(36, 50, 61, 0.08)',
        },
        '& .MuiAvatar-root.MuiAvatar-colorDefault': {
          background: 'var(--app-avatar-bg)',
          color: 'var(--app-avatar-color)',
          border: '1px solid var(--app-avatar-border)',
          boxShadow: 'var(--app-avatar-shadow)',
          backdropFilter: 'var(--app-avatar-blur)',
          WebkitBackdropFilter: 'var(--app-avatar-blur)',
          fontWeight: 500,
        },
        '& .MuiAvatar-root.MuiAvatar-colorDefault svg': {
          color: 'inherit',
        },
        '& .MuiTabs-root:not(.MuiTabs-vertical)': {
          borderBottomColor: 'rgba(36, 50, 61, 0.08)',
        },
        '& .MuiTabs-indicator': {
          display: 'none',
        },
        '& .MuiListItemButton-root:not(.Mui-disabled), & .MuiTab-root:not(.Mui-disabled)':
          {
            transition:
              'background-color 160ms ease, background 160ms ease, box-shadow 160ms ease, border-color 160ms ease, color 160ms ease',
            borderRadius: '8px',
            position: 'relative',
          },
        '& .MuiListItemButton-root:not(.Mui-disabled):hover, & .MuiTab-root:not(.Mui-disabled):hover':
          {
            background: 'var(--app-menu-hover-bg)',
            boxShadow: 'var(--app-menu-hover-shadow)',
            color: 'var(--app-hover-color)',
          },
        '& .MuiListItemButton-root.Mui-selected, & .MuiListItemButton-root.Mui-selected:hover, & .MuiTab-root.Mui-selected, & .MuiTab-root.Mui-selected:hover':
          {
            background: 'var(--app-menu-active-bg)',
            boxShadow: 'var(--app-menu-active-shadow)',
            color: 'var(--app-menu-active-color)',
          },
        '& .MuiListItemButton-root.Mui-selected::after': {
          content: '""',
          position: 'absolute',
          left: 6,
          top: 10,
          bottom: 10,
          width: 2,
          borderRadius: 4,
          backgroundColor: 'var(--app-active-accent)',
        },
        '& .MuiTab-root.Mui-selected::after': {
          content: '""',
          position: 'absolute',
          left: 18,
          right: 18,
          bottom: 6,
          height: 2,
          borderRadius: 4,
          backgroundColor: 'var(--app-active-accent)',
        },
        '& .MuiListItemButton-root.Mui-selected .MuiListItemText-primary, & .MuiListItemButton-root.Mui-selected .MuiListItemText-secondary, & .MuiListItemButton-root.Mui-selected svg':
          {
            color: 'var(--app-menu-active-color)',
          },
        '& .MuiOutlinedInput-root, & .MuiInputBase-root.MuiOutlinedInput-root':
          {
            backgroundColor: 'rgba(255, 255, 255, 0.58)',
          },
        '& .MuiButton-outlined, & .MuiButton-text': {
          backgroundColor: 'rgba(255, 255, 255, 0.46)',
          borderColor: 'rgba(255, 255, 255, 0.7)',
          borderRadius: '8px',
          lineHeight: 1.35,
          alignItems: 'center',
        },
        '& .MuiButton-outlined .MuiButton-startIcon, & .MuiButton-text .MuiButton-startIcon':
          {
            display: 'inline-flex',
            alignItems: 'center',
            marginLeft: 0,
          },
        '& .MuiButton-outlined svg, & .MuiButton-text svg': {
          flexShrink: 0,
        },
        '& .MuiPopover-paper, & .MuiMenu-paper': {
          backgroundColor: 'var(--app-glass-surface-strong)',
        },
      }
    : {}),

  transform: modalOpened ? 'scale(0.98)' : 'scale(1)',

  transition: open
    ? theme.transitions.create(['margin', 'transform'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      })
    : theme.transitions.create(['margin', 'transform'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
  marginLeft: !isMobile ? `${drawerWidth}px` : 0,
}))

const VerticalLayout = (props: LayoutProps) => {
  const { query } = useSearchQuery()
  const { children, scrollToTop } = props
  const [navState, setNavState] = React.useState(false)
  const { isMobile } = useResponsive()
  const modalOpened = Boolean(query.modal)
  const { initialize: initializeCommunicationService } = useSelectedAccounts(
    AccountsType.COMMUNICATION
  )
  const { initialize: initializeEmailService } = useSelectedAccounts(
    AccountsType.EMAIL
  )

  const isMobileWebView = useAccountSelect.isMobileWebView()
  const windowOpenRightSideBar = useAssistantSelect.windowOpenRightSideBar()
  const { leftNavTheme } = useLeftNavTheme()
  const isGlassTheme = leftNavTheme === 'light'
  const setNavHandle = () => {
    setNavState((prev) => !prev)
  }

  React.useEffect(() => {
    if (!isMobileWebView) {
      initializeEmailService()
      initializeCommunicationService()
    }
  }, [isMobileWebView])

  return (
    <>
      <LayoutContext.Provider value={{ navState, setNavHandle }}>
        <Stack
          direction="row"
          sx={{
            height: '100%',
            width: '100%',
            paddingRight: windowOpenRightSideBar ? '375px' : 0,
          }}
        >
          <SearchAsyncModal />
          <AssinstantWindow />
          <Navigation />

          <Main
            open={navState}
            isMobile={isMobile}
            modalOpened={modalOpened}
            isGlassTheme={isGlassTheme}
            data-interface-theme={leftNavTheme}
          >
            {!isMobileWebView && (
              <Stack
                pt={{ sm: 3, xl: 6 }}
                sx={{
                  marginLeft: (theme) => ({
                    xs: theme.spacing(3),
                    md: theme.spacing(6),
                  }),
                  marginRight: (theme) => ({
                    xs: theme.spacing(3),
                    md: theme.spacing(6),
                  }),
                }}
              >
                <GlobalSearchComponent />
              </Stack>
            )}

            <Stack
              pt={{ xs: 5, xl: 6 }}
              flex={1}
              minHeight={0}
              direction="column"
              sx={{
                transition: 'padding .25s ease-in-out',
                overflow: 'hidden',
              }}
            >
              {children}
            </Stack>
          </Main>
        </Stack>
      </LayoutContext.Provider>

      {scrollToTop ? (
        scrollToTop(props)
      ) : (
        <ScrollToTop className="mui-fixed">
          <Fab color="primary" size="small" aria-label="scroll back to top">
            <Icon icon="tabler:arrow-up" />
          </Fab>
        </ScrollToTop>
      )}
    </>
  )
}

export default VerticalLayout
